/** Вкладка «Товар» панели «Чатов»: что понравилось клиенту — на примерке, на сайте, в личном
 * кабинете — одной сеткой с выбором источника; ниже — что показывали менеджеры: поиски по фото,
 * отметки из каталога, фото из переписки без поиска. По всем чатам клиента — `GET /api/chats/{id}/client`.
 * Спеки `docs/specs/2026-10-03-chat-client-interest-design.md`, `docs/specs/2026-10-22-site-orders-interest-design.md`. */
import { useQuery } from "@tanstack/react-query";
import { Images, Search as SearchIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";

import type { OnOffer } from "@/components/chat/SerialPhotos";
import { SimilarDialog, type SimilarItem } from "@/components/chat/SimilarDialog";
import { TriedDialog } from "@/components/chat/TriedDialog";
import { dt } from "@/components/timeline/Timeline";
import {
  type Channel,
  type ClientDress,
  type ClientInterest,
  type ClientLiked,
  type ClientSearch,
  type LikedSrc,
  loadChatClient,
} from "@/lib/clientChats";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const chip = "rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px]";
const title = "mb-2 text-[13px] font-medium";
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const CHANNEL: Record<Channel, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  max: "MAX",
  avito: "Авито",
  vk: "ВК",
  instagram: "Инста",
  livechat: "Сайт",
};
const HOW: Record<string, string> = { hover: "наведение", viewer: "кнопка в просмотре", panel: "кнопка панели" };
const SOURCE: Record<ClientSearch["source"], string> = {
  chat: "«Чаты»",
  ext: "расширение",
  site: "клиент сам на сайте",
  top: "клиент сам, топ салона",
};
const REPEAT: Record<string, string> = { like: "лайк", salon: "салон", picked: "отметка", catalog: "каталог", site: "сайт" };
const SRC: { id: LikedSrc; label: string; short: string }[] = [
  { id: "fit", label: "Примерка", short: "Примерка" },
  { id: "site", label: "Сайт", short: "Сайт" },
  { id: "lk", label: "Личный кабинет", short: "ЛК" },
];
const KIND: Record<string, string> = {
  like: "понравилось",
  deal: "в сделку",
  client_like: "клиент: like",
  order: "заказ",
  wish: "сердечко",
  selection: "лайк в подборке",
  top: "лайк в топе",
};
const VIA = { browser: "тот же браузер, что оформил заказ", account: "та же учётка сайта, что оформила заказ" };
const HOUR = 3600_000;
const SIZE_FROM = { salon: "мерили в салоне", filter: "из фильтра", text: "назвал сам" };

export const clientQuery = (chatId: number) => ({
  queryKey: ["chat-client", chatId],
  queryFn: () => loadChatClient(chatId),
  staleTime: 30_000,
});

/** Сколько следов интереса у клиента — число на вкладке. */
export const clientTraces = (d: ClientInterest | undefined) =>
  d
    ? d.searches.length +
      d.catalog.length +
      d.summary.counts.likes +
      d.summary.counts.salon_liked +
      (d.summary.counts.site_liked ?? 0)
    : 0;

const dressTitle = (d: { name: string; serial: number | null }) =>
  [d.name, d.serial ? `серия ${d.serial}` : ""].filter(Boolean).join(" · ");

const box = "aspect-[3/4] w-full rounded-[8px] bg-black/5 object-cover";

/** Плашка поверх фото: свободной серии модели в салонах нет. Фото не гасим — по нему ищут похожие. */
function Out({ at = "bottom-1 left-1" }: { at?: string }) {
  return (
    <span className={`pointer-events-none absolute ${at} rounded-full bg-black/60 px-1.5 text-[10.5px] leading-[16px] text-white`}>
      нет в наличии
    </span>
  );
}

function Photo({ src, alt, out }: { src: string | null; alt: string; out?: boolean }) {
  if (!src) return <div className={box} />;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="relative block">
      <img src={src} alt={alt} loading="lazy" className={box} />
      {out && <Out />}
    </a>
  );
}

function Dress({ d, mark, children }: { d: ClientDress; mark?: string | undefined; children?: ReactNode }) {
  return (
    <li className="min-w-0">
      <div className={mark ? "rounded-[10px] ring-2 ring-[var(--color-accent)]" : ""}>
        <Photo src={d.photo} alt={dressTitle(d)} out={!d.in_stock} />
      </div>
      {mark && <div className="text-accent-text">{mark}</div>}
      <div className="truncate leading-tight" title={dressTitle(d)}>
        {dressTitle(d) || "—"}
      </div>
      {d.price ? (
        <div className="text-fg-faint">
          {d.sale ? "продажа " : ""}
          {rub(d.price)}
        </div>
      ) : (
        !d.in_stock && <div className="text-fg-faint">нет в салоне</div>
      )}
      {children}
    </li>
  );
}

/** Чип чата, откуда событие: у события из другого чата клиента — ссылка на него. */
function From({ chatId, channel, web, current }: { chatId: number | null; channel: Channel | null; web: boolean; current: number }) {
  if (!chatId || !channel) return null;
  const label = `${CHANNEL[channel]}${web ? " из браузера" : ""}`;
  if (chatId === current) return <span className="text-fg-faint">{label}</span>;
  return (
    <Link to={`/chats/${chatId}`} className="text-accent-text hover:underline">
      {label}
    </Link>
  );
}

function Summary({ d }: { d: ClientInterest }) {
  const s = d.summary;
  const chips: ReactNode[] = [
    ...s.types.map((t) => (
      <span key={`t${t.type}`} className={chip} title={t.strong ? "по отмеченным платьям" : "по найденным похожим"}>
        {t.type}
        {s.types.length > 1 ? ` ${Math.round(t.share * 100)}%` : ""}
        {t.strong ? "" : " · по похожим"}
      </span>
    )),
    ...s.sizes.map((x) => (
      <span key={`s${x.value}`} className={chip} title={SIZE_FROM[x.from]}>
        размер {x.value}
      </span>
    )),
    ...s.colors.map((c) => (
      <span key={`c${c.value}`} className={chip}>
        {c.value}
      </span>
    )),
    s.budget && (
      <span key="b" className={chip}>
        {s.budget.min === s.budget.max ? rub(s.budget.min) : `${rub(s.budget.min)} – ${rub(s.budget.max)}`}
      </span>
    ),
    ...s.cities.map((c) => (
      <span key={`g${c.id}`} className={chip} title={`поисков в городе: ${c.n}`}>
        {c.name}
      </span>
    )),
  ].filter(Boolean);
  return (
    <div className={card} data-annot="клиент: что ищет">
      <div className={title}>Что ищет</div>
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">{chips}</div>
      ) : (
        <div className="text-fg-muted">По отмеченному и найденному пока не понять.</div>
      )}
      <div className="mt-2 text-fg-muted">
        поисков по фото {s.counts.searches} · лайков {s.counts.likes} · в салоне понравилось {s.counts.salon_liked} ·
        на сайте {s.counts.site_liked ?? 0} · предложено {s.counts.offered}
      </div>
      {s.repeats.length > 0 && (
        <>
          <div className="mb-1 mt-2 text-fg-muted">Повторяется в разных местах</div>
          <ul className="grid grid-cols-3 gap-2">
            {s.repeats.map((r) => (
              <Dress key={r.model_id} d={r}>
                <div className="text-fg-faint">{r.sources.map((x) => REPEAT[x] ?? x).join(" + ")}</div>
              </Dress>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const NO_CAT = "-";
/** Наличие и индекс фото — только платья (категория 32): у аксессуаров ни того, ни другого. */
const isDress = (c: ClientLiked) => !!c.category?.startsWith("Платья");
const catOf = (c: ClientLiked) => c.category || NO_CAT;
const catName = (k: string) => (k === NO_CAT ? "Без категории" : k);

/** Откуда карточка — коротко на фото: «Примерка · Сайт 6 · ЛК». */
const whence = (c: ClientLiked) =>
  SRC.map((s) => [s.short, c.sources.filter((x) => x.src === s.id).length] as const)
    .filter(([, n]) => n > 0)
    .map(([label, n]) => (n > 1 ? `${label} ${n}` : label));

/** Всё, что раньше было подписью под фото, — строками: подсказка на фото и шапка окна похожих. */
function details(c: ClientLiked, crm: boolean): string[] {
  const about = [
    c.category,
    c.serial ? `серия ${c.serial}` : "",
    c.size,
    c.price ? `${c.sale ? "продажа " : ""}${rub(c.price)}` : "",
    crm && isDress(c) ? (c.in_stock ? `есть${c.city ? `: ${c.city}` : ""}` : "нет в наличии") : "",
  ].filter(Boolean);
  return [
    about.join(" · "),
    ...c.sources.map((x) =>
      [
        `${SRC.find((s) => s.id === x.src)?.label} · ${KIND[x.kind] ?? x.kind}${x.order_id ? ` ${x.order_id}` : ""}`,
        x.at ? dt(x.at) : "",
        x.user ?? "",
        x.via ? VIA[x.via] : "",
      ]
        .filter(Boolean)
        .join(" · "),
    ),
  ].filter(Boolean);
}

/** Карточка «понравилось» — только фото, все одной высоты: на фото «откуда» и «нет в наличии»,
 * остальное — подсказкой. Клик — окно похожих (у карточки без модели CRM искать не от чего). */
function LikedCard({ c, crm, onPick }: { c: ClientLiked; crm: boolean; onPick: () => void }) {
  const can = !!c.model_id && !!c.photo;
  const tip = [...details(c, crm), can ? "Нажмите — похожие по фото" : "Модель не найдена в CRM — похожих не найти"];
  return (
    <li className="min-w-0">
      <button
        type="button"
        disabled={!can}
        onClick={onPick}
        title={tip.join("\n")}
        className="group relative block w-full overflow-hidden rounded-[8px] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-default"
        data-annot="товар: найти похожие"
      >
        {c.photo ? <img src={c.photo} alt="" loading="lazy" className={box} /> : <div className={box} />}
        {crm && isDress(c) && !c.in_stock && <Out at="left-1 top-1" />}
        {can && (
          <span className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <SearchIcon size={13} />
          </span>
        )}
        <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 text-left text-[10.5px] leading-[14px] text-white">
          {whence(c).join(" · ")}
        </span>
      </button>
    </li>
  );
}

/** Карточка «понравилось» → вещь окна похожих: ищем от модели CRM, подписи — `details`. */
const similarItem = (c: ClientLiked, crm: boolean): SimilarItem => ({
  key: c.key,
  photo: c.photo,
  of: c.model_id && c.photo ? { model: c.model_id } : null,
  title: c.category ?? null,
  lines: details(c, crm),
  note: isDress(c) ? null : "По фото сравниваем только платья салонов — у этой вещи смотрите «Связи CRM».",
});

/** «Понравилось»: кнопки «Примерка · Сайт · Личный кабинет» (нажаты все, в адресе `&src=`; последнюю
 * нажатую не отжать), категория (`&cat=`) и карточки группами по категориям каталога CRM в порядке их
 * `sortOrder` (платья — свадебные, вечерние, выпускные), внутри — свежие первыми. Клик по фото —
 * окно похожих; отмеченные там серии — фото с номером в поле ответа. «Похожие» (есть сделка и
 * отметки примерки) — похожие на всё, что клиент мерил: коллаж и ссылка на страницу (`TriedDialog`). */
function Liked({ d, chatId, onOffer }: { d: ClientInterest; chatId: number; onOffer: OnOffer }) {
  const [params, setParams] = useSearchParams();
  const [pick, setPick] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const liked = d.liked ?? [];
  const raw = (params.get("src") ?? "").split(",").filter((x): x is LikedSrc => SRC.some((s) => s.id === x));
  const on = new Set<LikedSrc>(raw.length ? raw : SRC.map((s) => s.id));
  const set = (key: string, value: string | null) =>
    setParams(
      (p) => {
        if (value === null) p.delete(key);
        else p.set(key, value);
        return p;
      },
      { replace: true },
    );
  const toggle = (id: LikedSrc) => {
    const next = new Set(on);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (next.size === 0) return;
    set("src", next.size === SRC.length ? null : SRC.filter((s) => next.has(s.id)).map((s) => s.id).join(","));
  };
  const bySrc = liked.filter((c) => c.sources.some((x) => on.has(x.src)));
  // Группы по порядку категорий CRM; sort устойчив — внутри группы остаётся «свежие первыми».
  const groups: [string, ClientLiked[]][] = [];
  for (const c of [...bySrc].sort((a, b) => (a.category_sort ?? 1e6) - (b.category_sort ?? 1e6))) {
    const last = groups[groups.length - 1];
    if (last && last[0] === catOf(c)) last[1].push(c);
    else groups.push([catOf(c), [c]]);
  }
  const cat = params.get("cat");
  const shownGroups = cat ? groups.filter(([k]) => k === cat) : groups;
  const flat = shownGroups.flatMap(([, list]) => list);
  const stale = d.site?.synced_at && Date.now() - new Date(d.site.synced_at).getTime() > HOUR;
  return (
    <div className={card} data-annot="товар: понравилось">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {SRC.map((s) => {
          const n = liked.filter((c) => c.sources.some((x) => x.src === s.id)).length;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggle(s.id)}
              aria-pressed={on.has(s.id)}
              className={`${chip} ${on.has(s.id) ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-white" : "text-fg-muted"}`}
            >
              {s.label} {n}
            </button>
          );
        })}
        {d.client.deal && liked.some((c) => c.sources.some((x) => x.src === "fit")) && (
          <button
            type="button"
            onClick={() => setTried(true)}
            className={`${chip} inline-flex items-center gap-1 text-accent-text hover:bg-bg`}
            title="Похожие на всё, что клиент мерил на примерке, которые есть в салоне: коллаж и ссылка на страницу — в поле ответа"
            data-annot="товар: похожие по примерке"
          >
            <Images size={13} /> Похожие
          </button>
        )}
        {groups.length > 1 && (
          <select
            value={cat ?? ""}
            onChange={(e) => set("cat", e.target.value || null)}
            className={`${chip} ml-auto max-w-[45%] bg-bg ${cat ? "border-[var(--color-accent)] text-fg" : "text-fg-muted"}`}
            aria-label="Категория"
            data-annot="товар: фильтр по категории"
          >
            <option value="">Все категории {bySrc.length}</option>
            {groups.map(([k, list]) => (
              <option key={k} value={k}>
                {catName(k)} {list.length}
              </option>
            ))}
            {cat && !groups.some(([k]) => k === cat) && <option value={cat}>{catName(cat)} 0</option>}
          </select>
        )}
      </div>
      {stale && <div className="mb-2 text-fg-muted">Сайт не обновлялся с {dt(d.site!.synced_at!)}.</div>}
      {flat.length > 0 ? (
        <div className="flex flex-col gap-3">
          {shownGroups.map(([k, list]) => (
            <section key={k} data-annot={`товар: группа ${catName(k)}`}>
              <div className="mb-1 text-[12px] font-medium text-fg-muted">
                {catName(k)} <span className="font-normal text-fg-faint">{list.length}</span>
              </div>
              <ul className="grid grid-cols-3 gap-1.5">
                {list.map((c) => (
                  <LikedCard key={c.key} c={c} crm={d.crm} onPick={() => setPick(flat.indexOf(c))} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <div className="text-fg-muted">{cat ? "В этой категории отметок нет." : "Здесь клиент пока ничего не отмечал."}</div>
      )}
      {tried && <TriedDialog chatId={chatId} onClose={() => setTried(false)} onOffer={onOffer} />}
      {pick !== null && flat[pick] && (
        <SimilarDialog
          list={flat.map((c) => similarItem(c, d.crm))}
          at={pick}
          location={d.client.location_id ?? null}
          onMove={setPick}
          onClose={() => setPick(null)}
          onOffer={onOffer}
        />
      )}
    </div>
  );
}

function Search({ s, chatId }: { s: ClientSearch; chatId: number }) {
  const o = s.offer;
  const how = s.source === "ext" && s.how ? `: ${HOW[s.how] ?? s.how}` : "";
  return (
    <div className="mt-3 border-t border-border pt-3 first:mt-0 first:border-0 first:pt-0" data-annot="клиент: поиск по фото">
      <div className="flex gap-2">
        <div className="w-[72px] flex-none">
          {s.photo.url ? (
            <Photo src={s.photo.url} alt="фото клиента" />
          ) : (
            <div className="flex aspect-[3/4] w-full items-center rounded-[8px] bg-black/5 p-1 text-center text-[11px] text-fg-faint">
              {s.photo.text ? "по описанию" : s.source === "top" ? "топ салона" : "фото не сохранено"}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div>
            {SOURCE[s.source]}
            {how}
            {s.user ? ` · ${s.user}` : ""}
          </div>
          {s.photo.text && <div className="text-fg-muted">«{s.photo.text}»</div>}
          <div className="text-fg-muted">
            {dt(s.last)}
            {s.times > 1 ? ` · поисков: ${s.times}, первый ${dt(s.first)}` : ""}
          </div>
          <div className="text-fg-muted">
            {s.cities.length > 0 ? s.cities.map((c) => c.name).join(", ") : "все города"} · найдено {s.found}
          </div>
          <From chatId={s.chat_id} channel={s.channel} web={s.web} current={chatId} />
          {o && (
            <div className="mt-1">
              <a href={o.url} target="_blank" rel="noreferrer" className="text-accent-text hover:underline">
                Подборка: {o.count} шт.
              </a>
              {o.picked > 0 ? ` · отмечено ${o.picked}` : ""}
              {o.props.length > 0 ? ` · фильтр: ${o.props.join(", ")}` : ""}
              <div className={o.sent === "none" ? "text-fg-faint" : "text-[var(--color-success,#1a7f37)]"}>
                {o.sent === "link"
                  ? "ссылка отправлена клиенту"
                  : o.sent === "photos"
                    ? `отправлено фото: ${o.sent_photos}`
                    : "черновик взят, отправка не подтверждена"}
              </div>
              <div className="text-fg-muted">
                открытий: {o.open_count}
                {o.open_last ? `, последнее ${dt(o.open_last)}` : ""}
                {o.go_last ? ` · перешёл в ${CHANNEL[o.go_channel as Channel] ?? o.go_channel} ${dt(o.go_last)}` : ""}
              </div>
            </div>
          )}
        </div>
      </div>
      {s.top.length > 0 && (
        <ul className="mt-2 grid grid-cols-5 gap-1.5 text-[11px]">
          {s.top.map((d) => (
            <Dress key={d.model_id} d={d} mark={d.picked ? "отмечено" : undefined} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function ClientTab({
  chatId,
  onSearch,
  onCatalog,
  onOffer,
}: {
  chatId: number;
  /** «Найти похожие» по фото из переписки — окно поиска родителя. */
  onSearch: (chatId: number, messageId: number) => void;
  onCatalog: () => void;
  /** Отмеченные похожие — фото с номером серии в поле ответа. */
  onOffer: OnOffer;
}) {
  const q = useQuery(clientQuery(chatId));
  const d = q.data;
  if (!d) return <div className="p-4 text-[12.5px] text-fg-muted">{q.isError ? "Не удалось прочитать данные клиента." : "Загрузка…"}</div>;
  const empty = clientTraces(d) === 0 && d.unsearched.length === 0 && d.bot.length === 0;
  return (
    <div className="flex flex-col gap-3 p-3" data-annot="вкладка «Товар»">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-fg-muted">
        {d.client.deal && (
          <Link to={`/deals/${d.client.deal.id}`} className="text-accent-text hover:underline">
            Сделка {d.client.deal.id} · {d.client.deal.stage}
          </Link>
        )}
        <span>чатов клиента: {d.client.chats.length}</span>
        {d.client.chats
          .filter((c) => !c.current)
          .map((c) => (
            <From key={c.id} chatId={c.id} channel={c.channel} web={c.web} current={chatId} />
          ))}
      </div>
      {!d.crm && <div className="text-[12.5px] text-fg-muted">CRM недоступна: салон, названия и цены не прочитаны.</div>}
      {empty ? (
        <div className={card}>
          <div className="text-fg-muted">
            Следов интереса пока нет: клиент не присылал фото, поиски по нему не делали, в салоне отметок нет.
          </div>
          <button type="button" onClick={onCatalog} className="mt-2 text-accent-text hover:underline">
            Открыть каталог
          </button>
        </div>
      ) : (
        <Summary d={d} />
      )}
      {!empty && <Liked d={d} chatId={chatId} onOffer={onOffer} />}
      {d.searches.length > 0 && (
        <div className={card} data-annot="клиент: поиски по фото">
          <div className={title}>Поиски по фото: {d.searches.length}</div>
          <div>
            {d.searches.map((s) => (
              <Search key={s.key} s={s} chatId={chatId} />
            ))}
          </div>
        </div>
      )}
      {d.catalog.length > 0 && (
        <div className={card} data-annot="клиент: из каталога">
          <div className={title}>Предложено из каталога</div>
          {d.catalog.map((c) => (
            <div key={c.at} className="mt-3 first:mt-0">
              <div className="text-fg-muted">
                {dt(c.at)}
                {c.user ? ` · ${c.user}` : ""}
              </div>
              {c.filter.length > 0 && <div>{c.filter.join(" · ")}</div>}
              <div className={`mb-1 ${c.sent_photos > 0 ? "text-[var(--color-success,#1a7f37)]" : "text-fg-faint"}`}>
                {c.sent_photos > 0 ? `отправлено фото: ${c.sent_photos}` : "отправка не подтверждена"}
              </div>
              <ul className="grid grid-cols-4 gap-2">
                {c.models.map((m) => (
                  <Dress key={m.model_id} d={m} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {d.unsearched.length > 0 && (
        <div className={card} data-annot="клиент: фото без поиска">
          <div className={title}>Фото клиента без поиска из «Чатов»: {d.unsearched.length}</div>
          <ul className="grid grid-cols-4 gap-2">
            {d.unsearched.map((u) => (
              <li key={u.message_id} className="min-w-0">
                <Photo src={u.photo} alt="фото клиента" />
                <div className="text-fg-faint">{dt(u.at)}</div>
                <From chatId={u.chat_id} channel={u.channel} web={u.web} current={chatId} />
                {u.photo ? (
                  <button type="button" onClick={() => onSearch(u.chat_id, u.message_id)} className="block text-accent-text hover:underline">
                    Найти похожие
                  </button>
                ) : (
                  <div className="text-fg-faint">{u.note}</div>
                )}
                {u.ext && <div className="text-fg-faint" title="Поиск расширения к сообщению не привязан">могли искать из расширения</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {d.bot.length > 0 && (
        <div className={card} data-annot="клиент: бот">
          <div className={title}>Пришёл в бота по платью</div>
          {d.bot.map((b) => (
            <div key={`${b.chat_id}-${b.at}`} className="text-fg-muted">
              {dt(b.at)} · {CHANNEL[b.channel]} · {b.kind === "serial" ? `платье серия ${b.serial ?? "?"}` : "по ссылке подборки"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
